import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { CandidateInput } from '../PollCreator/CandidateInput';
import { PollTimingInput } from '../PollCreator/PollTimingInput';
import { Toast } from '../Toast/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { hasDuplicateCandidates } from '../../utils/pollUtils';
import type { Poll, PollTiming } from '../../types';

export function EditPoll() {
  const { pollId } = useParams<{ pollId: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [poll, setPoll] = useState<Poll | null>(null);
  const [pollName, setPollName] = useState('');
  const [candidates, setCandidates] = useState<string[]>([]);
  const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null);
  const [allowNominations, setAllowNominations] = useState(false);
  const [showSharingIcons, setShowSharingIcons] = useState(false);
  const [singleUpvoteOnly, setSingleUpvoteOnly] = useState(false);
  const [timing, setTiming] = useState<PollTiming>({});
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchPoll = async () => {
      if (!pollId) return;

      try {
        setLoading(true);
        const { data: pollData, error: pollError } = await supabase
          .from('polls')
          .select('*')
          .eq('id', pollId)
          .single();

        if (pollError) throw pollError;
        if (!pollData) throw new Error('Poll not found');

        const { data: candidatesData, error: candidatesError } = await supabase
          .from('candidates')
          .select('*')
          .eq('poll_id', pollId)
          .order('created_at', { ascending: true });

        if (candidatesError) throw candidatesError;
        if (!candidatesData) throw new Error('No candidates found');

        setPoll({ ...pollData, candidates: candidatesData });
        setPollName(pollData.name);
        setCandidates(candidatesData.map(c => c.name));
        setAllowNominations(pollData.settings.allowNominations);
        setShowSharingIcons(pollData.settings.showSharingIcons);
        setSingleUpvoteOnly(pollData.settings.singleUpvoteOnly || false);
        setTiming({
          duration: pollData.settings.duration,
          expiresAt: pollData.settings.expiresAt
        });
      } catch (error) {
        console.error('Error fetching poll:', error);
        showToast('Failed to load poll');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchPoll();
  }, [pollId, navigate, showToast]);

  const hasDuplicates = hasDuplicateCandidates(candidates);
  const validCandidates = candidates.filter(c => c.trim() !== '');
  const canSubmit = !hasDuplicates && 
    pollName.trim() !== '' && 
    (allowNominations || validCandidates.length >= 2) &&
    !updating;

  const getDisabledReason = () => {
    if (updating) return "Updating poll...";
    if (!pollName.trim()) return "Please enter a poll name";
    if (hasDuplicates) return "Please remove duplicate candidates";
    if (!allowNominations && validCandidates.length < 2) return "Please add at least 2 candidates";
    return "";
  };

  const addCandidate = () => {
    const newIndex = candidates.length;
    setCandidates([...candidates, '']);
    setLastAddedIndex(newIndex);
  };

  const handleCandidateEnter = (index: number) => {
    if (index === candidates.length - 1) {
      addCandidate();
    }
  };

  const removeCandidate = (index: number) => {
    if (candidates.length <= 2) return;
    const newCandidates = [...candidates];
    newCandidates.splice(index, 1);
    setCandidates(newCandidates);
  };

  const updateCandidate = (index: number, value: string) => {
    const newCandidates = [...candidates];
    newCandidates[index] = value;
    setCandidates(newCandidates);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSubmit || !poll || !pollId) {
      return;
    }

    try {
      setUpdating(true);

      // 1. Update poll name and settings first
      const { error: pollUpdateError } = await supabase
        .from('polls')
        .update({
          name: pollName.trim(),
          settings: {
            ...poll.settings,
            allowNominations,
            showSharingIcons,
            singleUpvoteOnly: poll.settings.singleUpvoteOnly, // Keep original value
            duration: timing.duration,
            expiresAt: timing.expiresAt
          }
        })
        .eq('id', pollId);

      if (pollUpdateError) throw pollUpdateError;

      // 2. Handle candidate changes
      const existingCandidates = poll.candidates;
      const candidateUpdates = candidates.map((name, index) => ({
        id: index < existingCandidates.length ? existingCandidates[index].id : undefined,
        poll_id: pollId,
        name: name.trim()
      }));

      // Delete removed candidates
      if (existingCandidates.length > candidateUpdates.length) {
        const candidatesToDelete = existingCandidates.slice(candidateUpdates.length);
        const { error: deleteError } = await supabase
          .from('candidates')
          .delete()
          .in('id', candidatesToDelete.map(c => c.id));

        if (deleteError) throw deleteError;
      }

      // Update existing candidates
      for (let i = 0; i < Math.min(existingCandidates.length, candidateUpdates.length); i++) {
        if (existingCandidates[i].name !== candidateUpdates[i].name) {
          const { error: updateError } = await supabase
            .from('candidates')
            .update({ name: candidateUpdates[i].name })
            .eq('id', existingCandidates[i].id);

          if (updateError) throw updateError;
        }
      }

      // Add new candidates
      if (candidateUpdates.length > existingCandidates.length) {
        const newCandidates = candidateUpdates
          .slice(existingCandidates.length)
          .map(({ id, ...rest }) => rest);
        
        const { error: insertError } = await supabase
          .from('candidates')
          .insert(newCandidates);

        if (insertError) throw insertError;
      }

      showToast('Poll updated successfully');
      navigate(`/${pollId}/results`);
    } catch (error) {
      console.error('Error updating poll:', error);
      showToast('Failed to update poll');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-gray-600 dark:text-gray-400">Poll not found</div>
        </div>
      </div>
    );
  }

  if (poll.author_id !== userId) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="text-gray-600 dark:text-gray-400">You don't have permission to edit this poll</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h1 className="text-2xl font-bold mb-6 dark:text-white">Edit Poll</h1>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="pollName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Poll Name
                </label>
                <input
                  type="text"
                  id="pollName"
                  value={pollName}
                  onChange={(e) => setPollName(e.target.value)}
                  placeholder="Enter poll name"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Candidates
                </label>
                {candidates.map((candidate, index) => (
                  <CandidateInput
                    key={index}
                    value={candidate}
                    index={index}
                    showRemove={candidates.length > 2}
                    onChange={updateCandidate}
                    onRemove={removeCandidate}
                    onEnter={handleCandidateEnter}
                    autoFocus={index === lastAddedIndex}
                  />
                ))}
                
                <button
                  type="button"
                  onClick={addCandidate}
                  className="flex items-center gap-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Add Another Candidate
                </button>
              </div>
              
              <PollTimingInput
                timing={timing}
                onChange={setTiming}
              />

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="allowNominations"
                    checked={allowNominations}
                    onChange={(e) => setAllowNominations(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <label htmlFor="allowNominations" className="text-sm text-gray-700 dark:text-gray-300">
                    Allow voters to nominate additional candidates
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="showSharingIcons"
                    checked={showSharingIcons}
                    onChange={(e) => setShowSharingIcons(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  <label htmlFor="showSharingIcons" className="text-sm text-gray-700 dark:text-gray-300">
                    Show poll sharing icons to voters
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="singleUpvoteOnly"
                    checked={singleUpvoteOnly}
                    disabled={true}
                    className="rounded border-gray-300 dark:border-gray-600 cursor-not-allowed opacity-60"
                  />
                  <label 
                    htmlFor="singleUpvoteOnly" 
                    className="text-sm text-gray-700 dark:text-gray-300 cursor-not-allowed"
                    title="This option cannot be changed after the poll is created"
                  >
                    Each voter allowed one upvote
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/${pollId}/results`)}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  title={!canSubmit ? getDisabledReason() : "Update poll"}
                  className={`flex-1 py-3 rounded-lg text-white transition-colors ${
                    canSubmit 
                      ? 'bg-blue-500 hover:bg-blue-600' 
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {updating ? 'Updating...' : 'Update Poll'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
      {toast && <Toast message={toast} onDismiss={hideToast} />}
    </div>
  );
}